import { ComponentProps, ReactNode, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { useViewer, useViewerRoles } from '@treams/treams-hooks';
import { useUserQuery, useViewerWithManagersAndManageesQuery } from '@treams/treams-network';
import {
	Avatar,
	Button,
	CloseIcon,
	FlexGap,
	IconButton,
	Paragraph,
	Popover,
	Select,
	Spinner,
	StarsIcon,
	Title,
	UserSelect,
	ViewIcon,
	tokens,
} from '@treams/treams-ui';

import { AGENT_CHAT_SUGGESTION_KEYS } from '../agentChatSuggestions';
import { useAIAssistanceApproval } from '../useAIAssistanceApproval';
import { AgentChatAIApprovalCard } from './AgentChatAIApprovalCard';

export const SUGGESTIONS = [
	{ key: AGENT_CHAT_SUGGESTION_KEYS[0], icon: <ViewIcon /> },
	{ key: AGENT_CHAT_SUGGESTION_KEYS[1], icon: <ViewIcon /> },
	{ key: AGENT_CHAT_SUGGESTION_KEYS[2], icon: <ViewIcon /> },
] as const;

const EmptyStateContainer = styled(Flex).attrs<{ $compact?: boolean }>((props) => ({
	flexDirection: 'column',
	alignItems: props.$compact ? 'stretch' : 'center',
}))<{ $compact?: boolean }>`
	text-align: ${(props) => (props.$compact ? 'left' : 'center')};
	gap: ${tokens.gap.section}px;
	width: 100%;
	padding-block: ${(props) => (props.$compact ? tokens.padding.container : tokens.padding.page)}px;
	box-sizing: border-box;
`;

export const SuggestionButtonGroup = styled(FlexGap).attrs({
	gap: tokens.gap.list,
	flexWrap: 'wrap',
})<{ $faded?: boolean }>`
	${({ $faded }) =>
		$faded &&
		css`
			opacity: 0.5;
		`}
`;

export function SuggestionButton({
	suggestion,
	...props
}: {
	suggestion: { key: string; icon: ReactNode; text: string };
} & Omit<ComponentProps<typeof Button>, 'type' | 'icon' | 'title'>) {
	return <Button {...props} type="secondary" icon={suggestion.icon} title={suggestion.text} />;
}

export function AgentChatEmpty(props: {
	composer?: ReactNode;
	compact?: boolean;
	initialSubjectID?: string;
	onSelectSuggestion: (message: string, subjectID?: string) => void;
}) {
	const { t } = useTranslation();
	const viewer = useViewer();
	const roles = useViewerRoles();
	const { needsApproval } = useAIAssistanceApproval();
	const [selectedUserID, setSelectedUserID] = useState<string | undefined>(
		props.initialSubjectID ?? viewer?.id,
	);
	const [userSelectVisible, setUserSelectVisible] = useState(false);

	// Full-org search: SUPER_USER + HR_BUSINESS_PARTNER only.
	// Keep aligned with MCP access to other employees' people data
	// (Goal.viewUserGoals workspace roles in packages/@treams/goals + MCP tools).
	// ADMINISTRATOR is intentionally excluded — settings access ≠ employee file access.
	const canSearchAllUsers = roles.isWorkspaceSuperuser || roles.isHRBusinessPartner;
	const canChooseSubject = canSearchAllUsers || roles.isManager;

	const { data: relationsData, loading: relationsLoading } = useViewerWithManagersAndManageesQuery({
		fetchPolicy: 'cache-first',
		skip: canSearchAllUsers || !roles.isManager,
	});
	const loadingManagees = !canSearchAllUsers && roles.isManager && relationsLoading;

	const managees = useMemo(
		() =>
			(relationsData?.viewer.managees ?? []).reduce<
				NonNullable<typeof relationsData>['viewer']['managees']
			>((managees, managee) => {
				if (managee.isArchived) return managees;

				if (!managees.some((existing) => existing.id === managee.id)) {
					managees.push(managee);
				}

				return managees;
			}, []),
		[relationsData?.viewer.managees],
	);

	const manageeSelectOptions = useMemo(() => {
		if (canSearchAllUsers || !roles.isManager || !viewer?.id) {
			return [];
		}

		const options = [
			{
				title: t('aiAgent.subjectMe'),
				value: viewer.id,
			},
			...[...managees]
				.sort((a, b) => a.firstName.localeCompare(b.firstName))
				.map((managee) => ({
					title: `${managee.firstName} ${managee.lastName}`,
					value: managee.id,
				})),
		];

		return [
			{
				title: t('oneOnOne.meta.subjectGroups.managees'),
				options,
			},
		];
	}, [canSearchAllUsers, roles.isManager, viewer?.id, managees, t]);

	useEffect(() => {
		if (!props.initialSubjectID) {
			return;
		}

		setSelectedUserID((current) => {
			if (current && current !== viewer?.id && current !== props.initialSubjectID) {
				return current;
			}

			return props.initialSubjectID;
		});
	}, [props.initialSubjectID, viewer?.id]);

	const user = useUserQuery({ variables: { id: selectedUserID ?? '' } });

	const isViewingSelf = selectedUserID === viewer?.id;
	const selectedUser = user.data?.user;

	// Consent is per person: the viewer can opt in right here, but for anyone
	// else the chat can only explain that their data is off limits. For the
	// viewer we read the features query, since that is what the approval
	// mutation refetches; the User flag only settles on the next fetch.
	const suggestionsBlocked = isViewingSelf
		? needsApproval
		: !!selectedUser && !selectedUser.conversationAiInsightsApproved;

	const userName = useMemo(() => {
		if (selectedUserID === viewer?.id) {
			return t('aiAgent.subjectMe');
		}

		return [user.data?.user?.firstName, user.data?.user?.lastName].filter(Boolean).join(' ');
	}, [user.data?.user, selectedUserID, viewer?.id, t]);

	const subjectPickerContents = canSearchAllUsers ? (
		<UserSelect.Menu
			setSelected={(userID) => {
				setSelectedUserID(userID);
				setUserSelectVisible(false);
			}}
			placeholder={t('aiAgent.chooseSubject')}
			selectedUserID={selectedUserID}
			includeViewer={true}
		/>
	) : (
		<Select.Menu
			options={manageeSelectOptions}
			selectedValue={selectedUserID}
			onChange={(userID) => {
				if (userID) {
					setSelectedUserID(userID);
					setUserSelectVisible(false);
				}
			}}
			searchPlaceholder={t('aiAgent.chooseSubject')}
			autofocus
		/>
	);

	return (
		<EmptyStateContainer $compact={props.compact}>
			{!props.compact && (
				<FlexGap gap={tokens.gap.element} alignItems="center">
					<StarsIcon size={44} color={tokens.brand.icon} />
					<Title>{t('sidebar.aiAgent')}</Title>
				</FlexGap>
			)}
			{(roles.loading || loadingManagees) && <Spinner />}
			{!roles.loading && !loadingManagees && canChooseSubject && (
				<Flex alignItems="center">
					<Popover.Target
						visible={userSelectVisible}
						contents={subjectPickerContents}
						onDismiss={() => setUserSelectVisible(false)}
						autofocus={true}
					>
						<Select.Trigger
							fit
							leading={
								user.data?.user ? (
									<Avatar size={16} user={user.data?.user} />
								) : (
									<Avatar.Skeleton size={16} />
								)
							}
							leadingTint={false}
							onClick={() => setUserSelectVisible(true)}
						>
							<Trans
								i18nKey="aiAgent.forSubject"
								components={{ strong: <strong /> }}
								values={{ name: userName }}
							/>
						</Select.Trigger>
					</Popover.Target>
					{selectedUserID !== viewer?.id && (
						<IconButton
							ml={1}
							type="secondary"
							icon={<CloseIcon />}
							aria-label={t('common.close')}
							onClick={() => setSelectedUserID(viewer?.id)}
						/>
					)}
				</Flex>
			)}
			{isViewingSelf && <AgentChatAIApprovalCard />}
			{selectedUser &&
				(suggestionsBlocked && !isViewingSelf ? (
					<Paragraph>
						{t('aiAgent.subjectDoesNotShareData', {
							name: userName || t('aiAgent.thisUser'),
						})}
					</Paragraph>
				) : (
					<SuggestionButtonGroup
						justifyContent={props.compact ? 'flex-start' : 'center'}
						$faded={suggestionsBlocked}
					>
						{SUGGESTIONS.map((suggestion) => ({
							...suggestion,
							key: isViewingSelf ? suggestion.key + 'Me' : suggestion.key,
						}))
							.map((suggestion) => ({
								...suggestion,
								text: t(suggestion.key, { name: selectedUser.firstName }),
							}))
							.map((suggestion) => (
								<SuggestionButton
									key={suggestion.key}
									suggestion={suggestion}
									disabled={suggestionsBlocked}
									onClick={() =>
										props.onSelectSuggestion(
											suggestion.text,
											isViewingSelf ? undefined : selectedUserID,
										)
									}
								/>
							))}
					</SuggestionButtonGroup>
				))}
			{props.composer}
			{!props.composer && (
				<Paragraph small mb={0}>
					{t('aiAgent.disclaimer')}
				</Paragraph>
			)}
		</EmptyStateContainer>
	);
}
